<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import IspMetricCard from './components/IspMetricCard.vue';
import IspBarChart from './components/IspBarChart.vue';
// GK-HUB: Central conversation modal used by the Recorrência drill-down
import IspConversationDetailModal from './components/IspConversationDetailModal.vue';

import { useIspReport } from './composables/useIspReport';
import { formatTime, getDateLocale } from './composables/ispFormatters';
import { exportCsv } from './composables/useIspExport';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';
import './styles/isp-filters.css';

const { t, locale } = useI18n();
const store = useStore();

// GK-HUB: Removed showDeptFilter/departments/selectedDeptId — using team filter instead
const {
  from,
  to,
  data: recorrencia,
  retry,
  customDateRange,
  selectedDateRange,
  onDateRangeChange,
  onFilterChange,
  filters,
} = useIspReport({
  getter: 'ispReports/getRecorrencia',
  action: 'ispReports/fetchRecorrencia',
  filters: { motivo: '', page: '1', teamId: '' },
  initActions: ['ispReports/fetchMotivosList'],
});

// GK-HUB: Teams list + showTeamFilter (admin/manager only)
const teams = computed(() => store.getters['teams/getTeams']);
store.dispatch('teams/get');
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(
    getUserRole(currentUser.value, currentAccountId.value)
  )
);
// GK-HUB: end team filter

const ispMotivos = computed(() => store.getters['ispReports/getMotivosList']);

const isFetching = computed(() => recorrencia.value?.isFetching || false);
const reportData = computed(() => recorrencia.value?.data || null);
const summary = computed(() => reportData.value?.summary || {});
const highlightMotivo = computed(
  () => reportData.value?.highlight_motivo || ''
);
const pagination = computed(
  () =>
    reportData.value?.pagination || {
      current_page: 1,
      total_pages: 1,
      total_count: 0,
      per_page: 25,
    }
);
const alertas = computed(() => reportData.value?.alertas || []);

// Reset to page 1 when filter changes (except page itself)
const onFilterChangeWithReset = (key, value) => {
  if (key !== 'page') {
    filters.page.value = '1';
  }
  onFilterChange(key, value);
};

// Pagination
const showingFrom = computed(() => {
  const p = pagination.value;
  if (p.total_count === 0) return 0;
  return (p.current_page - 1) * p.per_page + 1;
});
const showingTo = computed(() => {
  const p = pagination.value;
  return Math.min(p.current_page * p.per_page, p.total_count);
});
const goToPage = page => {
  if (page < 1 || page > pagination.value.total_pages) return;
  onFilterChange('page', String(page));
};

// Distribution chart
const getDistribColor = idx => {
  const colors = ['blue', 'cyan', 'warning', 'error', 'error'];
  return colors[idx] || 'error';
};

const distribItems = computed(() =>
  (reportData.value?.distribuicao || []).map((d, i) => ({
    label: d.label,
    value: d.count,
    displayValue: String(d.count),
    percentage: d.percentage,
    color: getDistribColor(i),
  }))
);

// Por motivo chart — highlighted bar gets special color
const motivoBarItems = computed(() => {
  const items = reportData.value?.por_motivo || [];
  if (!items.length) return [];
  const maxConvs = Math.max(...items.map(m => m.total_convs), 1);
  return items.map(m => ({
    label: m.motivo,
    value: m.total_convs,
    displayValue: `${m.total_convs} ${t('ISP_REPORTS.RECORRENCIA.CONVS_LABEL')}`,
    percentage: (m.total_convs / maxConvs) * 100,
    color: m.highlighted ? 'success' : 'blue',
  }));
});

// Severity badge
const severityBadge = severity => {
  const map = {
    critical: {
      label: t('ISP_REPORTS.RECORRENCIA.SEVERITY_CRITICAL'),
      class: 'severity-badge--critical',
    },
    warning: {
      label: t('ISP_REPORTS.RECORRENCIA.SEVERITY_WARNING'),
      class: 'severity-badge--warning',
    },
    monitoring: {
      label: t('ISP_REPORTS.RECORRENCIA.SEVERITY_MONITORING'),
      class: 'severity-badge--monitoring',
    },
  };
  return map[severity] || map.monitoring;
};

// Date formatting
const formatDate = iso => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString(getDateLocale(locale.value), {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};
const formatFullDate = iso => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString(getDateLocale(locale.value), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Status badge
const statusBadge = status => {
  const map = {
    resolved: {
      label: t('ISP_REPORTS.STATUS.RESOLVED'),
      class: 'bg-n-teal-3 text-n-teal-11',
    },
    open: {
      label: t('ISP_REPORTS.STATUS.OPEN'),
      class: 'bg-n-blue-3 text-n-blue-11',
    },
    pending: {
      label: t('ISP_REPORTS.STATUS.PENDING'),
      class:
        'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    },
  };
  return (
    map[status] || { label: status, class: 'bg-n-solid-3 text-n-slate-10' }
  );
};

// Slide-over state
const selectedContactId = ref(null);
const detailData = computed(
  () => store.getters['ispReports/getRecorrenciaDetail']
);
const detailFetching = computed(() => detailData.value?.isFetching || false);
const detailContact = computed(() => detailData.value?.data?.contact || null);
const detailConversations = computed(
  () => detailData.value?.data?.conversations || []
);

const openSlideOver = contactId => {
  selectedContactId.value = contactId;
  store.dispatch('ispReports/fetchRecorrenciaDetail', {
    contactId,
    from: from.value,
    to: to.value,
    teamId: filters.teamId.value || undefined,
  });
};

const closeSlideOver = () => {
  selectedContactId.value = null;
};

// GK-HUB: Central conversation modal (drill-down from the drawer)
const selectedConversationId = ref(null);
const openConversationModal = conv => {
  selectedConversationId.value = conv.id;
};
const closeConversationModal = () => {
  selectedConversationId.value = null;
};

// Close on ESC key — modal first, then drawer
const onKeydown = e => {
  if (e.key !== 'Escape') return;
  if (selectedConversationId.value) {
    closeConversationModal();
  } else {
    closeSlideOver();
  }
};
watch(selectedContactId, val => {
  document.removeEventListener('keydown', onKeydown);
  if (val) {
    document.addEventListener('keydown', onKeydown);
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
});

// CSV export — when motivo highlighted, export only matching clients
const onExportCsv = () => {
  const rows = highlightMotivo.value
    ? alertas.value.filter(r => r.highlighted)
    : alertas.value;
  const header =
    'Cliente,Telefone,Contatos,Motivo,Motivos,Último Contato,Severidade\n';
  const mapFn = r =>
    [
      r.name,
      r.phone || '',
      r.count,
      r.motivo || '',
      r.motivos || '',
      r.last_contact_at ? formatDate(r.last_contact_at) : '',
      r.severity,
    ]
      .map(v => `"${String(v).replace(/"/g, '""')}"`)
      .join(',');
  const filename = highlightMotivo.value
    ? `recorrencia-${highlightMotivo.value.replace(/\s/g, '_')}.csv`
    : 'recorrencia-clientes.csv';
  exportCsv(rows, header, mapFn, filename);
};

// Export button label
const exportLabel = computed(() => {
  if (highlightMotivo.value) {
    const count = alertas.value.filter(r => r.highlighted).length;
    return `${t('ISP_REPORTS.FILTERS.EXPORT_CSV')} (${count})`;
  }
  return t('ISP_REPORTS.FILTERS.EXPORT_CSV');
});
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.RECORRENCIA.HEADER') }}
      </h1>
      <p class="text-[13px] text-n-slate-10 mt-0.5">
        {{ t('ISP_REPORTS.RECORRENCIA.SUBTITLE') }}
      </p>
    </div>

    <!-- Filters bar -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <!-- Date range picker -->
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <!-- GK-HUB: Team filter (replaces department) -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select
          :value="filters.teamId.value"
          class="isp-select"
          @change="onFilterChangeWithReset('teamId', $event.target.value)"
        >
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_TEAMS') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: end team filter -->
      <!-- Motivo highlight filter -->
      <div class="isp-select-wrap">
        <select
          :value="filters.motivo.value"
          class="isp-select"
          :class="{ 'isp-select--active': filters.motivo.value }"
          @change="onFilterChangeWithReset('motivo', $event.target.value)"
        >
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_REASONS') }}
          </option>
          <option v-for="motivo in ispMotivos" :key="motivo" :value="motivo">
            {{ motivo.replace(/_/g, ' ') }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- Active filter indicator -->
      <span
        v-if="highlightMotivo"
        class="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full highlight-pill"
      >
        <span class="i-lucide-focus w-3 h-3" />
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        Destacando: {{ highlightMotivo }}
        <!-- eslint-enable vue/no-bare-strings-in-template -->
        <button
          class="i-lucide-x w-3 h-3 hover:opacity-100 opacity-60 cursor-pointer"
          @click="onFilterChangeWithReset('motivo', '')"
        />
      </span>
      <!-- Export CSV -->
      <button class="btn-export ml-auto" @click="onExportCsv">
        <span class="i-lucide-download w-3.5 h-3.5" />
        {{ exportLabel }}
      </button>
    </div>

    <!-- Loading (initial only) -->
    <woot-loading-state
      v-if="isFetching && !reportData"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error (initial only) -->
    <div
      v-else-if="recorrencia.error && !reportData"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2 cursor-pointer"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="reportData">
      <!-- KPI Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <IspMetricCard
          :label="t('ISP_REPORTS.RECORRENCIA.CLIENTES_ATENDIDOS')"
          :value="summary.total_clientes || 0"
          :trend="t('ISP_REPORTS.RECORRENCIA.NO_PERIODO')"
          trend-direction="neutral"
          :loading="isFetching"
        />
        <IspMetricCard
          :label="`${t('ISP_REPORTS.RECORRENCIA.RECORRENTES')} (2+)`"
          :value="summary.recorrentes || 0"
          :trend="`${summary.recorrentes_pct || 0}% ${t('ISP_REPORTS.RECORRENCIA.DO_TOTAL')}`"
          trend-direction="neutral"
          value-color="text-amber-500"
          :loading="isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.RECORRENCIA.FREQ_MEDIA')"
          :value="`${summary.frequencia_media || 0}x`"
          :trend="
            summary.freq_trend
              ? `${summary.freq_trend > 0 ? '+' : ''}${summary.freq_trend} ${t('ISP_REPORTS.PAINEL.VS_ANTERIOR')}`
              : ''
          "
          :trend-direction="
            summary.freq_trend > 0
              ? 'down'
              : summary.freq_trend < 0
                ? 'up'
                : 'neutral'
          "
          :loading="isFetching"
        />
      </div>

      <!-- Charts: 60/40 layout -->
      <div
        class="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 items-stretch mb-6"
      >
        <!-- Distribution (60%) -->
        <IspBarChart
          :title="t('ISP_REPORTS.RECORRENCIA.DISTRIBUICAO')"
          :items="distribItems"
        />
        <!-- By Reason (40%) -->
        <IspBarChart
          :title="t('ISP_REPORTS.RECORRENCIA.POR_MOTIVO')"
          :subtitle="t('ISP_REPORTS.RECORRENCIA.POR_MOTIVO_SUBTITLE')"
          :items="motivoBarItems"
          label-width="120px"
        />
      </div>

      <!-- Clients table -->
      <div
        v-if="alertas.length || pagination.total_count > 0"
        class="bg-n-solid-2 border border-n-weak rounded-[10px] overflow-hidden"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
        >
          <div
            class="text-[13px] font-semibold text-n-slate-12 flex items-center gap-2"
          >
            {{ t('ISP_REPORTS.RECORRENCIA.ALERTAS_TITLE') }}
            <span class="font-normal text-n-slate-9 text-xs">
              {{ t('ISP_REPORTS.RECORRENCIA.ALERTAS_SUBTITLE') }}
            </span>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-[13px] min-w-[700px]">
            <thead>
              <tr class="border-b border-n-weak bg-n-solid-3">
                <th
                  class="text-left font-semibold text-n-slate-10 px-3 py-3 w-16"
                >
                  {{ t('ISP_REPORTS.RECORRENCIA.COL_COUNT') }}
                </th>
                <th class="text-left font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.RECORRENCIA.COL_NAME') }}
                </th>
                <th class="text-left font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.RECORRENCIA.COL_PHONE') }}
                </th>
                <th class="text-left font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.RECORRENCIA.COL_MOTIVO') }}
                </th>
                <th
                  class="text-left font-semibold text-n-slate-10 px-3 py-3 whitespace-nowrap"
                >
                  {{ t('ISP_REPORTS.RECORRENCIA.COL_LAST_CONTACT') }}
                </th>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <th
                  class="text-center font-semibold text-n-slate-10 px-3 py-3 w-24"
                >
                  Status
                </th>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
                <th class="w-8" />
              </tr>
            </thead>
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <tbody>
              <tr
                v-for="alerta in alertas"
                :key="alerta.contact_id"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors cursor-pointer"
                :class="{ 'highlighted-row': alerta.highlighted }"
                @click="openSlideOver(alerta.contact_id)"
              >
                <!-- Count -->
                <td class="px-3 py-3">
                  <span
                    class="text-base font-bold tabular-nums"
                    :class="{
                      'text-n-ruby-11': alerta.severity === 'critical',
                      'text-amber-500': alerta.severity === 'warning',
                      'text-n-blue-11': alerta.severity === 'monitoring',
                    }"
                  >
                    {{ alerta.count
                    }}{{ t('ISP_REPORTS.RECORRENCIA.TIMES_SUFFIX') }}
                  </span>
                </td>
                <!-- Name -->
                <td class="px-3 py-3">
                  <div
                    class="font-medium text-n-slate-12 truncate max-w-[180px]"
                  >
                    {{ alerta.name }}
                  </div>
                </td>
                <!-- Phone -->
                <td class="px-3 py-3 text-n-slate-10 whitespace-nowrap">
                  {{ alerta.phone || '-' }}
                </td>
                <!-- Top motivo -->
                <td class="px-3 py-3 text-n-slate-11 truncate max-w-[140px]">
                  {{ alerta.motivo || '-' }}
                </td>
                <!-- Last contact -->
                <td
                  class="px-3 py-3 text-n-slate-10 whitespace-nowrap tabular-nums"
                >
                  {{ formatDate(alerta.last_contact_at) }}
                </td>
                <!-- Severity badge -->
                <td class="px-3 py-3 text-center">
                  <span
                    class="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    :class="severityBadge(alerta.severity).class"
                  >
                    {{ severityBadge(alerta.severity).label }}
                  </span>
                </td>
                <!-- Chevron -->
                <td class="px-2 py-3">
                  <span class="i-lucide-chevron-right w-4 h-4 text-n-slate-8" />
                </td>
              </tr>
            </tbody>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
          </table>
        </div>

        <!-- Empty state inside table -->
        <div
          v-if="!alertas.length && !isFetching"
          class="flex flex-col items-center justify-center py-16 gap-2"
        >
          <span class="i-lucide-users w-8 h-8 text-n-slate-7" />
          <p class="text-sm text-n-slate-9">
            {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_NO_DATA') }}
          </p>
        </div>

        <!-- Pagination -->
        <div
          v-if="alertas.length"
          class="flex items-center justify-between px-4 py-3 border-t border-n-weak"
        >
          <span class="text-[12px] text-n-slate-9">
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            Exibindo {{ showingFrom }} a {{ showingTo }} de
            {{ pagination.total_count }}
            <!-- eslint-enable vue/no-bare-strings-in-template -->
          </span>
          <div class="flex items-center gap-1">
            <button
              class="px-2.5 py-1 text-[12px] rounded-md border border-n-weak text-n-slate-10 hover:bg-n-solid-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              :disabled="pagination.current_page <= 1"
              @click="goToPage(pagination.current_page - 1)"
            >
              {{ t('ISP_REPORTS.PAGINATION.PREVIOUS') }}
            </button>
            <span class="text-[12px] text-n-slate-9 px-2 tabular-nums">
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              {{ pagination.current_page }} / {{ pagination.total_pages }}
              <!-- eslint-enable vue/no-bare-strings-in-template -->
            </span>
            <button
              class="px-2.5 py-1 text-[12px] rounded-md border border-n-weak text-n-slate-10 hover:bg-n-solid-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              :disabled="pagination.current_page >= pagination.total_pages"
              @click="goToPage(pagination.current_page + 1)"
            >
              {{ t('ISP_REPORTS.PAGINATION.NEXT') }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty State (no data at all) -->
    <div
      v-if="!isFetching && !recorrencia.error && !reportData"
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.EMPTY_RECORRENCIA_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.EMPTY_RECORRENCIA_MESSAGE') }}
      </p>
    </div>

    <!-- Slide-over overlay -->
    <Transition name="fade">
      <div
        v-if="selectedContactId"
        class="fixed inset-0 bg-black/30 z-40"
        @click="closeSlideOver"
      />
    </Transition>

    <!-- Slide-over panel -->
    <Transition name="slide">
      <div
        v-if="selectedContactId"
        class="fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-n-solid-2 border-l border-n-weak shadow-xl overflow-y-auto"
      >
        <!-- Loading -->
        <div
          v-if="detailFetching && !detailContact"
          class="flex items-center justify-center h-64"
        >
          <woot-loading-state :message="t('ISP_REPORTS.LOADING')" />
        </div>

        <template v-if="detailContact">
          <!-- Header -->
          <div
            class="flex items-center justify-between px-5 py-4 border-b border-n-weak sticky top-0 bg-n-solid-2 z-10"
          >
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_TITLE') }}
              </h2>
            </div>
            <button
              class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-n-solid-3 transition-colors cursor-pointer"
              @click="closeSlideOver"
            >
              <span class="i-lucide-x w-4 h-4 text-n-slate-10" />
            </button>
          </div>

          <!-- Contact info -->
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <div class="px-5 py-4 border-b border-n-weak">
            <div class="flex items-center gap-3">
              <!-- eslint-disable vue/no-static-inline-styles -->
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0"
                style="background: linear-gradient(135deg, #1b6b93, #5dade2)"
              >
                <!-- eslint-enable vue/no-static-inline-styles -->
                {{
                  detailContact.name
                    ? detailContact.name.charAt(0).toUpperCase()
                    : '?'
                }}
              </div>
              <div class="min-w-0">
                <div class="font-medium text-n-slate-12 truncate">
                  {{ detailContact.name }}
                </div>
                <div
                  class="text-[12px] text-n-slate-9 truncate flex items-center gap-3"
                >
                  <span
                    v-if="detailContact.phone"
                    class="flex items-center gap-1"
                  >
                    <span class="i-lucide-phone w-3 h-3" />
                    {{ detailContact.phone }}
                  </span>
                  <span
                    v-if="detailContact.email"
                    class="flex items-center gap-1"
                  >
                    <span class="i-lucide-mail w-3 h-3" />
                    {{ detailContact.email }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- eslint-enable vue/no-bare-strings-in-template -->

          <!-- Stats grid 2x2 -->
          <div class="px-5 py-4 border-b border-n-weak">
            <div class="grid grid-cols-2 gap-3">
              <div class="bg-n-solid-3 rounded-lg p-3">
                <div
                  class="text-[10px] uppercase tracking-wider text-n-slate-9 mb-1"
                >
                  {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_TOTAL_CONTACTS') }}
                </div>
                <div class="text-base font-bold text-n-slate-12 tabular-nums">
                  {{ detailContact.total_contacts }}
                </div>
              </div>
              <div class="bg-n-solid-3 rounded-lg p-3">
                <div
                  class="text-[10px] uppercase tracking-wider text-n-slate-9 mb-1"
                >
                  {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_FREQUENCY') }}
                </div>
                <div class="text-base font-bold text-n-blue-11 tabular-nums">
                  {{ detailContact.total_contacts
                  }}{{ t('ISP_REPORTS.RECORRENCIA.TIMES_SUFFIX') }}
                </div>
              </div>
              <div class="bg-n-solid-3 rounded-lg p-3">
                <div
                  class="text-[10px] uppercase tracking-wider text-n-slate-9 mb-1"
                >
                  {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_FIRST_CONTACT') }}
                </div>
                <div class="text-sm font-medium text-n-slate-11 tabular-nums">
                  {{ formatFullDate(detailContact.first_contact_at) }}
                </div>
              </div>
              <div class="bg-n-solid-3 rounded-lg p-3">
                <div
                  class="text-[10px] uppercase tracking-wider text-n-slate-9 mb-1"
                >
                  {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_LAST_CONTACT') }}
                </div>
                <div class="text-sm font-medium text-n-slate-11 tabular-nums">
                  {{ formatFullDate(detailContact.last_contact_at) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Motivos breakdown -->
          <div
            v-if="
              detailContact.motivos_breakdown &&
              detailContact.motivos_breakdown.length
            "
            class="px-5 py-4 border-b border-n-weak"
          >
            <h3
              class="text-[11px] uppercase tracking-wider font-medium text-n-slate-9 mb-3"
            >
              {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_MOTIVOS_BREAKDOWN') }}
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="mb in detailContact.motivos_breakdown"
                :key="mb.motivo"
                class="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 bg-n-solid-3 text-n-slate-11 rounded-full"
              >
                {{ mb.motivo }}
                <span class="font-bold text-n-slate-12">
                  ({{ mb.count
                  }}{{ t('ISP_REPORTS.RECORRENCIA.TIMES_SUFFIX') }})
                </span>
              </span>
            </div>
          </div>

          <!-- Conversation history -->
          <div class="px-5 py-4">
            <h3
              class="text-[11px] uppercase tracking-wider font-medium text-n-slate-9 mb-3"
            >
              {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_CONVERSATIONS') }}
            </h3>
            <div
              v-if="!detailConversations.length"
              class="text-center text-sm text-n-slate-9 py-8"
            >
              {{ t('ISP_REPORTS.RECORRENCIA.DETAIL_NO_DATA') }}
            </div>
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <div v-else class="space-y-2">
              <button
                v-for="conv in detailConversations"
                :key="conv.id"
                type="button"
                class="w-full text-left flex items-center gap-3 p-3 bg-n-solid-3 rounded-lg hover:bg-n-alpha-2 transition-colors cursor-pointer"
                @click="openConversationModal(conv)"
              >
                <div
                  class="text-n-blue-11 font-medium text-[13px] w-14 flex-shrink-0"
                >
                  #{{ conv.display_id }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-[12px] text-n-slate-11 truncate">
                    {{ conv.motivo || '-' }}
                  </div>
                  <div
                    class="text-[11px] text-n-slate-9 flex items-center gap-2 mt-0.5"
                  >
                    <span>{{ formatFullDate(conv.created_at) }}</span>
                    <span
                      v-if="conv.agent_name"
                      class="inline-flex items-center gap-1 truncate"
                    >
                      {{ conv.agent_name }}
                      <!-- GK-HUB: IA badge next to agent name -->
                      <span v-if="conv.is_ia_agent" class="gk-ia-badge">
                        {{ $t('CONVERSATION.IA_BADGE') }}
                      </span>
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <span
                    v-if="conv.duration_seconds"
                    class="text-[11px] text-n-slate-9 tabular-nums"
                  >
                    {{ formatTime(conv.duration_seconds) }}
                  </span>
                  <span
                    class="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    :class="statusBadge(conv.status).class"
                  >
                    {{ statusBadge(conv.status).label }}
                  </span>
                  <span
                    class="i-lucide-chevron-right w-3.5 h-3.5 text-n-slate-9"
                  />
                </div>
              </button>
            </div>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
          </div>
        </template>
      </div>
    </Transition>

    <!-- GK-HUB: Central conversation modal (opens over the drawer) -->
    <IspConversationDetailModal
      :conversation-id="selectedConversationId"
      @close="closeConversationModal"
    />
  </div>
</template>

<style scoped>
/* GK-HUB: CSS-11 .isp-select styles moved to _isp-shared.scss */
/* Active state when motivo is selected (IspRecorrencia-specific) */
.isp-select--active {
  border-color: #10b981;
  background-color: rgba(16, 185, 129, 0.06);
}

/* ── Highlight pill ──────────────────────────────────────────────────── */
.highlight-pill {
  background-color: rgba(16, 185, 129, 0.1);
  color: #10b981;
  font-weight: 600;
}

/* ── Highlighted table row ───────────────────────────────────────────── */
.highlighted-row {
  background: linear-gradient(
    to right,
    rgba(16, 185, 129, 0.12),
    rgba(16, 185, 129, 0.02)
  );
  border-left: 3px solid #10b981;
}
.highlighted-row:hover {
  background: linear-gradient(
    to right,
    rgba(16, 185, 129, 0.18),
    rgba(16, 185, 129, 0.04)
  );
  border-left: 3px solid #10b981;
}

/* ── Export button: light mode ───────────────────────────────────────── */
.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1.5px solid #d1d5db;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  color: #6b7280;
}
.btn-export:hover {
  border-color: #9ca3af;
  color: #374151;
}

/* ── Severity badges ─────────────────────────────────────────────────── */
.severity-badge--critical {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}
.severity-badge--warning {
  background-color: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}
.severity-badge--monitoring {
  background-color: rgba(46, 134, 193, 0.1);
  color: #2e86c1;
}

/* ── Slide-over transitions ──────────────────────────────────────────── */
.slide-enter-active {
  transition: transform 300ms ease-out;
}
.slide-leave-active {
  transition: transform 200ms ease-in;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* ── Overlay fade ────────────────────────────────────────────────────── */
.fade-enter-active {
  transition: opacity 300ms ease-out;
}
.fade-leave-active {
  transition: opacity 200ms ease-in;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── DatePicker z-index fix ─────────────────────────────────────────── */
:deep(.date-picker) {
  z-index: 20;
}
</style>

<!-- GK-HUB: CSS-11 dark mode .isp-select styles moved to _isp-shared.scss -->
<style>
body.dark .isp-select--active {
  border-color: rgba(16, 185, 129, 0.5);
  background-color: rgba(16, 185, 129, 0.08);
}
/* ── Export button: dark mode ────────────────────────────────────────── */
body.dark .btn-export {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
body.dark .btn-export:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.85);
}
/* ── Severity badges: dark mode ──────────────────────────────────────── */
body.dark .severity-badge--critical {
  background-color: rgba(239, 68, 68, 0.15);
  color: #f87171;
}
body.dark .severity-badge--warning {
  background-color: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}
body.dark .severity-badge--monitoring {
  background-color: rgba(46, 134, 193, 0.12);
  color: #5dade2;
}
/* ── Highlight pill: dark mode ───────────────────────────────────────── */
body.dark .highlight-pill {
  background-color: rgba(16, 185, 129, 0.15);
  color: #34d399;
}
/* ── Highlighted row: dark mode ──────────────────────────────────────── */
body.dark .highlighted-row {
  background: linear-gradient(
    to right,
    rgba(16, 185, 129, 0.1),
    rgba(16, 185, 129, 0.02)
  );
  border-left: 3px solid #10b981;
}
body.dark .highlighted-row:hover {
  background: linear-gradient(
    to right,
    rgba(16, 185, 129, 0.15),
    rgba(16, 185, 129, 0.03)
  );
  border-left: 3px solid #10b981;
}
</style>
